import React, { useState, useEffect } from 'react';
import { FiPlus, FiEdit2, FiTrash2, FiHome, FiUpload, FiImage } from 'react-icons/fi';
import toast from 'react-hot-toast';
import api from '../../utils/api';

const RoomManagementContent = () => {
  const [rooms, setRooms] = useState([]);
  const [floors, setFloors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showImagesModal, setShowImagesModal] = useState(false);
  const [editingRoom, setEditingRoom] = useState(null);
  const [selectedRoom, setSelectedRoom] = useState(null);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRooms, setTotalRooms] = useState(0);
  const itemsPerPage = 10;
  
  // Filter states
  const [filters, setFilters] = useState({
    floorId: '',
    type: '',
    searchQuery: ''
  });
  const [showFilters, setShowFilters] = useState(false);

  const [formData, setFormData] = useState({
    floorId: '',
    roomNumber: '',
    name: '',
    type: 'single',
    amenities: [],
    description: '',
    fromRoom: '',
    toRoom: ''
  });
  const [amenityInput, setAmenityInput] = useState('');

  const roomTypes = [
    { value: 'single', label: 'Single' },
    { value: 'double', label: 'Double' },
    { value: 'deluxe', label: 'Deluxe' },
    { value: 'suite', label: 'Suite' },
    { value: 'dormitory', label: 'Dormitory' },
    { value: 'premium', label: 'Premium' },
    { value: 'standard', label: 'Standard' },
    { value: 'banquet', label: 'Banquet' }

  ];

  useEffect(() => {
    fetchData();
  }, [currentPage, filters]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append('page', currentPage);
      params.append('limit', itemsPerPage);
      
      // Add filters
      if (filters.floorId) params.append('floorId', filters.floorId);
      if (filters.type) params.append('type', filters.type);
      if (filters.searchQuery) params.append('search', filters.searchQuery);
      
      const [roomsResponse, floorsResponse] = await Promise.all([
        api.get(`/vendor/room-management/rooms?${params.toString()}`),
        api.get('/vendor/room-management/floors')
      ]);
      
      setRooms(roomsResponse.data.rooms || roomsResponse.data);
      setFloors(floorsResponse.data);
      
      // Set pagination info if available
      if (roomsResponse.data.pagination) {
        setTotalPages(roomsResponse.data.pagination.totalPages);
        setTotalRooms(roomsResponse.data.pagination.total);
      } else {
        setTotalPages(1);
        setTotalRooms(roomsResponse.data.length);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setCurrentPage(1); // Reset to first page when filters change
  };

  const clearFilters = () => {
    setFilters({
      floorId: '',
      type: '',
      searchQuery: ''
    });
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Validation: Either roomNumber should be provided OR both fromRoom and toRoom
      const hasRoomNumber = formData.roomNumber.trim();
      const hasFromTo = formData.fromRoom.trim() && formData.toRoom.trim();
      
      if (!hasRoomNumber && !hasFromTo) {
        toast.error('Either provide a Room Number or both From and To room numbers');
        return;
      }
      
      if (hasRoomNumber && hasFromTo) {
        toast.error('Provide either a single Room Number OR From/To range, not both');
        return;
      }
      
      // Check if from/to is provided
      if (formData.fromRoom && formData.toRoom) {
        const fromNum = parseInt(formData.fromRoom);
        const toNum = parseInt(formData.toRoom);
        
        if (fromNum > toNum) {
          toast.error('From room number cannot be greater than to room number');
          return;
        }
        
        // Create multiple rooms in a loop
        const roomsToCreate = [];
        for (let i = fromNum; i <= toNum; i++) {
          roomsToCreate.push({
            ...formData,
            roomNumber: i.toString(),
            fromRoom: '',
            toRoom: ''
          });
        }
        
        // Create rooms one by one
        for (const roomData of roomsToCreate) {
          await api.post('/vendor/room-management/rooms', roomData);
        }
        
        toast.success(`Created ${toNum - fromNum + 1} rooms successfully`);
        setShowAddModal(false);
      } else {
        // Single room creation (either roomNumber or from/to)
        const dataToSubmit = {
          ...formData
        };
        
        // Clear from/to fields for single room
        dataToSubmit.fromRoom = '';
        dataToSubmit.toRoom = '';

        if (editingRoom) {
          await api.put(`/vendor/room-management/rooms/${editingRoom._id}`, dataToSubmit);
          toast.success('Room updated successfully');
          setShowEditModal(false);
        } else {
          await api.post('/vendor/room-management/rooms', dataToSubmit);
          toast.success('Room created successfully');
          setShowAddModal(false);
        }
      }
      
      resetForm();
      fetchData();
    } catch (error) {
      console.error('Error saving room:', error);
      toast.error(error.response?.data?.message || 'Failed to save room');
    }
  };

  const handleEdit = (room) => {
    setEditingRoom(room);
    setFormData({
      floorId: room.floorId._id,
      roomNumber: room.roomNumber,
      name: room.name || '',
      type: room.type,
      amenities: room.amenities || [],
      description: room.description || '',
      fromRoom: '',
      toRoom: ''
    });
    setShowEditModal(true);
  };

  const handleDelete = async (roomId) => {
    if (!window.confirm('Are you sure you want to delete this room?')) return;
    
    try {
      await api.delete(`/vendor/room-management/rooms/${roomId}`);
      toast.success('Room deleted successfully');
      fetchData();
    } catch (error) {
      console.error('Error deleting room:', error);
      toast.error(error.response?.data?.message || 'Failed to delete room');
    }
  };

  const handleStatusChange = async (room) => {
    const statusOptions = ['available', 'booked', 'occupied', 'maintenance', 'out_of_order'];
    const currentIndex = statusOptions.indexOf(room.status);
    const nextStatus = statusOptions[(currentIndex + 1) % statusOptions.length];
    
    try {
      await api.patch(`/vendor/room-management/rooms/${room._id}/status`, { status: nextStatus });
      toast.success(`Room status changed to ${nextStatus.replace('_', ' ')}`);
      fetchData();
    } catch (error) {
      console.error('Error changing room status:', error);
      toast.error(error.response?.data?.message || 'Failed to change room status');
    }
  };

  const handleAddAmenity = () => {
    if (amenityInput.trim() && !formData.amenities.includes(amenityInput.trim())) {
      setFormData({
        ...formData,
        amenities: [...formData.amenities, amenityInput.trim()]
      });
      setAmenityInput('');
    }
  };

  const removeAmenity = (amenity) => {
    setFormData({
      ...formData,
      amenities: formData.amenities.filter(a => a !== amenity)
    });
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    try {
      const formData = new FormData();
      files.forEach(file => {
        formData.append('images', file);
      });

      const response = await api.post(`/vendor/room-management/rooms/${selectedRoom._id}/images`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success('Images uploaded successfully');
      fetchData();
      setSelectedRoom({ ...selectedRoom, images: response.data.images });
    } catch (error) {
      console.error('Error uploading images:', error);
      toast.error('Failed to upload images');
    }
  };

  const resetForm = () => {
    setFormData({
      floorId: '',
      roomNumber: '',
      name: '',
      type: 'single',
      amenities: [],
      description: '',
      fromRoom: '',
      toRoom: ''
    });
    setAmenityInput('');
    setEditingRoom(null);
    setShowAddModal(false);
    setShowEditModal(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48 sm:h-64">
        <div className="animate-spin rounded-full h-10 w-10 sm:h-12 sm:w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-4 sm:mb-6">
        <div>
          <h2 className="text-lg sm:text-xl font-semibold text-gray-800">Room Management</h2>
          <p className="text-gray-600 text-sm sm:text-base">Create and manage rooms on each floor</p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="bg-blue-600 text-white px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg flex items-center justify-center hover:bg-blue-700 transition-colors text-sm sm:text-base w-full sm:w-auto"
        >
          <FiPlus className="mr-2 w-4 h-4" />
          <span>Add Room</span>
        </button>
      </div>

      {/* Filters Section */}
      <div className="bg-white rounded-lg shadow mb-4 sm:mb-6">
        <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-200">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900">Filters</h3>
            <div className="flex flex-col sm:flex-row gap-2">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="px-3 sm:px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors"
              >
                {showFilters ? 'Hide Filters' : 'Show Filters'}
              </button>
              <button
                onClick={clearFilters}
                className="px-3 sm:px-4 py-2 text-sm font-medium text-gray-600 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors"
              >
                Clear All
              </button>
            </div>
          </div>
        </div>
        
        {showFilters && (
          <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-200">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
              {/* Search */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 sm:mb-2">Search</label>
                <input
                  type="text"
                  value={filters.searchQuery}
                  onChange={(e) => handleFilterChange('searchQuery', e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      e.stopPropagation();
                    }
                  }}
                  placeholder="Search by room number, name..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                />
              </div>
              
              {/* Floor Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 sm:mb-2">Floor</label>
                <select
                  value={filters.floorId}
                  onChange={(e) => handleFilterChange('floorId', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                >
                  <option value="">All Floors</option>
                  {floors.map((floor) => (
                    <option key={floor._id} value={floor._id}>{floor.name}</option>
                  ))}
                </select>
              </div>
              
              {/* Type Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 sm:mb-2">Room Type</label>
                <select
                  value={filters.type}
                  onChange={(e) => handleFilterChange('type', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                >
                  <option value="">All Types</option>
                  {roomTypes.map((type) => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        )}
        
        {/* Active Filters Display */}
        <div className="px-4 sm:px-6 py-2 sm:py-3">
          <div className="flex flex-wrap gap-2">
            {filters.searchQuery && (
              <span className="inline-flex items-center px-2 sm:px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                <span className="hidden sm:inline">Search: </span>{filters.searchQuery}
                <button
                  onClick={() => handleFilterChange('searchQuery', '')}
                  className="ml-1 sm:ml-2 text-blue-600 hover:text-blue-800"
                >
                  ×
                </button>
              </span>
            )}
            {filters.floorId && (
              <span className="inline-flex items-center px-2 sm:px-3 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                <span className="hidden sm:inline">Floor: </span>{floors.find(f => f._id === filters.floorId)?.name}
                <button
                  onClick={() => handleFilterChange('floorId', '')}
                  className="ml-1 sm:ml-2 text-purple-600 hover:text-purple-800"
                >
                  ×
                </button>
              </span>
            )}
            {filters.type && (
              <span className="inline-flex items-center px-2 sm:px-3 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                <span className="hidden sm:inline">Type: </span>{roomTypes.find(t => t.value === filters.type)?.label}
                <button
                  onClick={() => handleFilterChange('type', '')}
                  className="ml-1 sm:ml-2 text-orange-600 hover:text-orange-800"
                >
                  ×
                </button>
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Rooms List */}
      <div className="bg-white rounded-lg shadow">
        {rooms.length === 0 ? (
          <div className="p-6 sm:p-8 text-center">
            <FiHome className="mx-auto w-12 h-12 sm:w-16 sm:h-16 text-gray-400 mb-4" />
            <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-2">No rooms found</h3>
            <p className="text-gray-500 text-sm sm:text-base mb-4">Get started by creating your first room</p>
            <button
              onClick={() => setShowAddModal(true)}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors text-sm sm:text-base"
            >
              Create Room
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Room
                  </th>
                  <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Floor
                  </th>
                  <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {rooms.map((room) => (
                  <tr key={room._id} className="hover:bg-gray-50">
                    <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <FiHome className="text-blue-600 mr-2 w-4 h-4 sm:w-5 sm:h-5" />
                        <div>
                          <div className="text-sm font-medium text-gray-900">{room.roomNumber}</div>
                          {room.name && (
                            <div className="text-sm text-gray-500 truncate max-w-[120px] sm:max-w-none">{room.name}</div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-900">{room.floorId?.name || 'N/A'}</span>
                    </td>
                    <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-900 capitalize">{room.type}</span>
                    </td>
                    <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center space-x-1 sm:space-x-2">
                        <button
                          onClick={() => handleEdit(room)}
                          className="text-blue-600 hover:text-blue-900 p-1"
                        >
                          <FiEdit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(room._id)}
                          className="text-red-600 hover:text-red-900 p-1"
                        >
                          <FiTrash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="bg-white rounded-lg shadow px-4 sm:px-6 py-3 sm:py-4 mt-4 sm:mt-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="text-sm text-gray-700 text-center sm:text-left">
              <span className="hidden sm:inline">
                Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, totalRooms)} of {totalRooms} rooms
              </span>
              <span className="sm:hidden">
                {Math.min(currentPage * itemsPerPage, totalRooms)} of {totalRooms} rooms
              </span>
            </div>
            <div className="flex items-center justify-center sm:justify-end space-x-1 sm:space-x-2">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="px-2 sm:px-3 py-2 text-xs sm:text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span className="hidden sm:inline">Previous</span>
                <span className="sm:hidden">‹</span>
              </button>
              
              {/* Page Numbers */}
              <div className="flex items-center space-x-1">
                {Array.from({ length: Math.min(3, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage <= 2) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 1) {
                    pageNum = totalPages - 2 + i;
                  } else {
                    pageNum = currentPage - 1 + i;
                  }
                  
                  return (
                    <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      className={`min-w-[28px] sm:min-w-[36px] px-2 sm:px-3 py-2 text-xs sm:text-sm font-medium rounded-lg ${
                        currentPage === pageNum
                          ? 'bg-blue-600 text-white'
                          : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>
              
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="px-2 sm:px-3 py-2 text-xs sm:text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span className="hidden sm:inline">Next</span>
                <span className="sm:hidden">›</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Room Modal */}
      {(showAddModal || showEditModal) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-2 sm:p-4">
  
  <div
    className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] flex flex-col overflow-hidden"
    onClick={(e) => e.stopPropagation()}
  >

    {/* HEADER */}
    <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white p-4 sm:p-6 border-b sticky top-0 z-10">
      <div className="flex justify-between items-center">
        <h3 className="text-lg sm:text-xl font-bold">
          {editingRoom ? 'Edit Room' : 'Add New Room'}
        </h3>
        <button
          className="text-white hover:bg-white/20 rounded-lg p-2 transition"
          onClick={resetForm}
        >
          <span className="text-xl sm:text-2xl">×</span>
        </button>
      </div>
    </div>

    {/* FORM */}
    <form onSubmit={handleSubmit} className="flex flex-col flex-1">

      {/* SCROLL AREA */}
      <div className="p-4 sm:p-6 overflow-y-auto flex-1">

        {/* Floor */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1 sm:mb-2">
            Floor *
          </label>
          <select
            required
            value={formData.floorId}
            onChange={(e) => setFormData({ ...formData, floorId: e.target.value })}
            className="w-full px-3 sm:px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
          >
            <option value="">Select Floor</option>
            {floors.map((floor) => (
              <option key={floor._id} value={floor._id}>
                {floor.name}
              </option>
            ))}
          </select>
        </div>

        {/* Room Info */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4 mb-4">
          <input
            type="text"
            value={formData.roomNumber}
            onChange={(e) => setFormData({ ...formData, roomNumber: e.target.value })}
            placeholder="Room Number"
            className="w-full px-3 sm:px-4 py-2 border rounded-lg text-sm"
          />
          <input
            type="text"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="Room Name"
            className="w-full px-3 sm:px-4 py-2 border rounded-lg text-sm"
          />
        </div>

        {/* Range */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4 mb-4">
          <input
            type="text"
            inputMode="numeric"
            value={formData.fromRoom}
            onChange={(e) => {
              const val = e.target.value;
              if (val === '' || /^\d*$/.test(val)) {
                setFormData({ ...formData, fromRoom: val });
              }
            }}
            placeholder="From Room"
            className="w-full px-3 sm:px-4 py-2 border rounded-lg text-sm"
          />
          <input
            type="text"
            inputMode="numeric"
            value={formData.toRoom}
            onChange={(e) => {
              const val = e.target.value;
              if (val === '' || /^\d*$/.test(val)) {
                setFormData({ ...formData, toRoom: val });
              }
            }}
            placeholder="To Room"
            className="w-full px-3 sm:px-4 py-2 border rounded-lg text-sm"
          />
        </div>

        {/* Type */}
        <div className="mb-4">
          <select
            required
            value={formData.type}
            onChange={(e) => setFormData({ ...formData, type: e.target.value })}
            className="w-full px-3 sm:px-4 py-2 border rounded-lg text-sm"
          >
            {roomTypes.map((type) => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </select>
        </div>

        {/* Amenities */}
        <div className="mb-4">
          <div className="flex flex-col sm:flex-row gap-2 mb-2">
            <input
              type="text"
              value={amenityInput}
              onChange={(e) => setAmenityInput(e.target.value)}
              className="flex-1 px-3 sm:px-4 py-2 border rounded-lg text-sm"
              placeholder="Add amenity"
            />
            <button
              type="button"
              onClick={handleAddAmenity}
              className="px-4 py-2 bg-gray-600 text-white rounded-lg text-sm"
            >
              Add
            </button>
          </div>

          <div className="flex flex-wrap gap-2">
            {formData.amenities.map((a) => (
              <span
                key={a}
                className="flex items-center px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs"
              >
                {a}
                <button onClick={() => removeAmenity(a)} className="ml-1">×</button>
              </span>
            ))}
          </div>
        </div>

        {/* Description */}
        <textarea
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          className="w-full px-3 sm:px-4 py-2 border rounded-lg text-sm"
          rows="3"
          placeholder="Description"
        />

      </div>

      {/* FOOTER (FIXED) */}
      <div className="bg-white px-4 sm:px-6 py-3 sm:py-4 border-t flex flex-col sm:flex-row justify-end gap-2 sm:gap-3 sticky bottom-0 z-10 shadow-inner">
        <button
          type="button"
          onClick={resetForm}
          className="px-4 py-2 border rounded-lg text-gray-600 text-sm"
        >
          Cancel
        </button>
        <button
          type="submit"
          className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm"
        >
          {editingRoom ? 'Update Room' : 'Add Room'}
        </button>
      </div>

    </form>
  </div>
</div>
      )}

      {/* Images Modal */}
      {showImagesModal && selectedRoom && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-2 sm:p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white p-4 sm:p-6 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <h3 className="text-lg sm:text-xl font-bold">Room Images</h3>
                <button
                  className="text-white hover:bg-white hover:bg-opacity-20 rounded-lg p-2 transition-colors duration-200"
                  onClick={() => setShowImagesModal(false)}
                >
                  <span className="text-xl sm:text-2xl">×</span>
                </button>
              </div>
            </div>
            
            <div className="p-4 sm:p-6 overflow-y-auto max-h-[calc(90vh-80px)]">
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                  Upload Images
                </label>
                <input
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                />
              </div>

              {selectedRoom.images && selectedRoom.images.length > 0 && (
                <div className="mb-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Current Images</h4>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-3">
                    {selectedRoom.images.map((image, index) => (
                      <img
                        key={index}
                        src={image.startsWith('http') ? image : `${process.env.REACT_APP_API_URL}${image}`}
                        alt={`Room ${index + 1}`}
                        className="w-full h-20 sm:h-24 object-cover rounded-lg"
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="bg-gray-50 px-4 sm:px-6 py-3 sm:py-4 border-t border-gray-200 flex justify-end">
              <button
                onClick={() => setShowImagesModal(false)}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 text-sm"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RoomManagementContent;
